import { Fragment, type ReactNode, type Ref } from 'react';
import { Star } from 'lucide-react';
import { TableActionsMenu } from '../data-table/table-actions-menu.tsx';
import { SettingsRow } from '../settings/settings-row.tsx';
import { TypePill } from '../type-pill/type-pill.tsx';
import type { MeetingTemplateListLabels } from './meeting-template-list-labels.ts';
import type { MeetingTemplateRow } from './meeting-template-types.ts';
import { templateColorVariant } from './template-color-variant.ts';
import { TemplateRowNote } from './template-row-note.tsx';

export interface TemplateListRowProps {
  template: MeetingTemplateRow;
  labels: MeetingTemplateListLabels;
  canMakeDefault: boolean;
  onOpen: (templateId: string) => void;
  onMakeDefault?: (templateId: string) => void;
  /* The grip, on a row in a list this reader may arrange. */
  handle?: ReactNode;
  ref?: Ref<HTMLDivElement>;
  dragTransform?: string;
  dragTransition?: string;
  dragging?: boolean;
}

/**
 * One template as a row: its name in the pill it wears everywhere else, a few
 * quiet notes, and the whole row opens it. What a row offers is read off the
 * row, never off a role.
 */
export function TemplateListRow({
  template,
  labels,
  canMakeDefault,
  onOpen,
  onMakeDefault,
  handle,
  ref,
  dragTransform,
  dragTransition,
  dragging,
}: TemplateListRowProps) {
  const notes = [
    template.kind && template.kind !== 'one_on_one' && labels.kinds[template.kind],
    template.isDefault &&
      (template.kind ? labels.defaultMarkers[template.kind] : labels.defaultMarker),
    template.personal && labels.personalMarker,
    template.audience,
  ].filter((note): note is string => Boolean(note));

  const notesAside =
    notes.length > 0 ? (
      <span>
        {notes.map((note, index) => (
          <Fragment key={note}>
            {index > 0 && (
              <span aria-hidden='true' className='text-sm text-ink-muted'>
                {' · '}
              </span>
            )}
            <TemplateRowNote>{note}</TemplateRowNote>
          </Fragment>
        ))}
      </span>
    ) : undefined;

  function rowActions(): ReactNode {
    if (!canMakeDefault || !onMakeDefault) return undefined;
    if (template.isDefault || template.defaultable === false) {
      return <span aria-hidden='true' className='block size-7' />;
    }
    return (
      <TableActionsMenu
        label={labels.rowActions({ name: template.name })}
        items={[
          {
            label: labels.makeDefault,
            icon: <Star aria-hidden='true' />,
            onSelect: () => onMakeDefault(template.id),
          },
        ]}
      />
    );
  }

  return (
    <SettingsRow
      ref={ref}
      className='py-2'
      handle={handle}
      dragTransform={dragTransform}
      dragTransition={dragTransition}
      dragging={dragging}
      onOpen={() => onOpen(template.id)}
      label={
        <TypePill type={templateColorVariant(template.color)} size='sm'>
          {template.name}
        </TypePill>
      }
      labelAside={notesAside}
    >
      {rowActions()}
    </SettingsRow>
  );
}
